import { ApiError, useUpdateSuggestedChangeMutation, SyncUser } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import {
  DATE_FORMAT,
  DELAY_BEFORE_HIDE_ICON_DONE,
  SuggestedChangeDataSet,
} from '@schooly/constants';
import { DoneIcon, Spin, SyncIcon } from '@schooly/style';
import { Button, Icon, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useProfile } from '../../../context/profile/useProfile';
import { getUserFullName } from '../../../helpers/users';
import useFlag from '../../../hooks/useFlag';
import IntlError from '../../../utils/intlError';
import {
  getChangeValue,
  getRelatedChanges,
  getSuggestedChangeTypeId,
  hasPermissionForAcceptChanges,
} from './utils';

import { format } from 'date-fns';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getSchoolUserRoleTextId } from 'apps/web/src/helpers/misc';

export interface AcceptChangePopoverProps {
  dataSet?: SuggestedChangeDataSet;
  fieldName?: keyof SyncUser;
  canShow: boolean;
}

export const AcceptChangePopover = ({ dataSet, fieldName, canShow }: AcceptChangePopoverProps) => {
  const { palette } = useTheme();
  const [isIconDoneShow, showIconDone, hideIconDone] = useFlag();
  const { schoolId, permissions } = useAuth();
  const { showError } = useNotifications();
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();
  const updateChange = useUpdateSuggestedChangeMutation();
  const { user, userType, suggestedChanges, actions } = useProfile();

  const invalidateQueries = useInvalidateListQueriesFor(userType || 'student');

  const canAccept = hasPermissionForAcceptChanges(userType, permissions);

  const relatedChange = useMemo(() => {
    return getRelatedChanges(suggestedChanges, dataSet, fieldName);
  }, [dataSet, fieldName, suggestedChanges]);

  const [fromValue, toValue] = useMemo(
    () => getChangeValue(dataSet, fieldName, relatedChange, user as SyncUser),
    [dataSet, fieldName, relatedChange, user],
  );

  const currentValueInfo = relatedChange?.current_value_info;

  const applyChange = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!relatedChange || !schoolId) {
      return;
    }

    try {
      await updateChange.mutateAsync({ action: 'accept', schoolId, changeId: relatedChange.id });
      actions.invalidateProfileCache();
      invalidateQueries();

      showIconDone();

      setTimeout(() => hideIconDone(), DELAY_BEFORE_HIDE_ICON_DONE);
    } catch (e) {
      showError(e as ApiError | IntlError);
    }
  };

  const isLoading =
    updateChange.isLoading && updateChange.variables?.changeId === relatedChange?.id;

  const rejectChange = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!relatedChange || !schoolId) {
      return;
    }

    const isConfirmed = await getConfirmation({
      textId: 'suggestedChange-KeepCurrentConfirm',
    });

    if (!isConfirmed) {
      return;
    }

    try {
      await updateChange.mutateAsync({ action: 'reject', schoolId, changeId: relatedChange.id });
      actions.invalidateProfileCache();
      invalidateQueries();
      showIconDone();

      setTimeout(() => hideIconDone(), DELAY_BEFORE_HIDE_ICON_DONE);
    } catch (e) {
      showError(e as ApiError | IntlError);
    }
  };

  if (!canShow) {
    return null;
  }

  if (!userType || userType === 'adult' || userType === 'child' || !relatedChange) {
    return null;
  }

  const renderIcon = () => {
    if (isLoading) return <Spin />;

    return <SyncIcon color={palette.warning.main} />;
  };

  if (isIconDoneShow)
    return (
      <Icon sx={{ ml: 1 }}>
        <DoneIcon />
      </Icon>
    );

  return (
    <Tooltip
      arrow
      open={isLoading === true ? true : undefined}
      componentsProps={{
        tooltip: {
          sx: {
            minWidth: currentValueInfo ? 400 : 200,
            padding: 1.25,
          },
        },
      }}
      title={
        <>
          <Stack flexDirection="row" gap={1}>
            {currentValueInfo ? (
              <Stack flex={1} gap={1} alignItems="flex-start" justifyContent="space-between">
                <Stack gap={1} alignItems="flex-start">
                  <Stack>
                    <Typography color="text.primary">
                      {formatMessage({ id: 'suggestedChange-CurrentValue' })}{' '}
                      {formatMessage({
                        id: getSchoolUserRoleTextId(currentValueInfo.last_modified_by.role),
                      }).toLowerCase()}
                    </Typography>
                    <Typography color="text.primary">
                      {getUserFullName(currentValueInfo.last_modified_by)}{' '}
                      {format(
                        newDateTimezoneOffset(currentValueInfo.last_modified_at),
                        DATE_FORMAT,
                      )}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="caption" color="text.secondary">
                      <FormattedMessage id={getSuggestedChangeTypeId(dataSet, fieldName)} />
                    </Typography>
                    <Typography variant="h3">{fromValue || '—'}</Typography>
                  </Stack>
                </Stack>
                {canAccept && (
                  <Button
                    variant="outlined"
                    disabled={isLoading}
                    size="small"
                    onClick={rejectChange}
                  >
                    <FormattedMessage id="action-KeepCurrent" />
                  </Button>
                )}
              </Stack>
            ) : null}
            <Stack flex={1} gap={1} alignItems="flex-start" justifyContent="space-between">
              <Stack gap={1} alignItems="flex-start">
                <Stack>
                  <Typography color="text.primary">
                    {formatMessage({ id: 'suggestedChange-ProposedValue' })}{' '}
                    {formatMessage({
                      id: getSchoolUserRoleTextId(relatedChange.initiator_role),
                    }).toLowerCase()}
                  </Typography>

                  <Typography color="text.primary">
                    {getUserFullName(relatedChange.initiator)}{' '}
                    {format(newDateTimezoneOffset(relatedChange.created_at), DATE_FORMAT)}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id={getSuggestedChangeTypeId(dataSet, fieldName)} />
                  </Typography>
                  <Typography variant="h3">{toValue}</Typography>
                </Stack>
              </Stack>
              {canAccept && (
                <Button size="small" disabled={isLoading} onClick={applyChange}>
                  <FormattedMessage id="action-Apply" />
                </Button>
              )}
            </Stack>
          </Stack>
          {!canAccept && (
            <Stack pt={1.5} pb={0.5}>
              <Typography color="text.secondary">
                {formatMessage({ id: 'suggestedChange-NoAccess' })}
              </Typography>
            </Stack>
          )}
        </>
      }
    >
      <IconButton
        sx={{ ml: 1 }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {renderIcon()}
      </IconButton>
    </Tooltip>
  );
};
